import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OpportunityCard = ({ opportunity, onSave, onApply, onViewDetails }) => {
  const [isSaved, setIsSaved] = useState(opportunity?.isSaved || false);

  const handleSave = () => {
    setIsSaved(!isSaved);
    onSave(opportunity?.id, !isSaved);
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 75) return 'text-blue-600 bg-blue-50';
    if (percentage >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getCauseIcon = (cause) => {
    const iconMap = {
      'Animal Welfare': 'Heart',
      'Children & Youth': 'Users',
      'Education': 'GraduationCap',
      'Environment': 'Leaf',
      'Health & Wellness': 'Activity',
      'Homelessness': 'Home',
      'Senior Citizens': 'Users',
      'Arts & Culture': 'Palette',
      'Community Development': 'Building2',
      'Disaster Relief': 'AlertTriangle'
    };
    return iconMap?.[cause] || 'Heart';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={opportunity?.image || "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8dm9sdW50ZWVyfGVufDB8fDB8fHww"}
          alt={opportunity?.imageAlt || "Volunteer Opportunity"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={handleSave}
            className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
            aria-label={isSaved ? 'Remove from saved' : 'Save opportunity'}
          >
            <Icon
              name={isSaved ? 'Bookmark' : 'Bookmark'}
              size={18}
              color={isSaved ? '#2D5A27' : '#6B7280'}
              fill={isSaved ? '#2D5A27' : 'none'}
            />
          </button>
          <div className={`px-3 py-1.5 rounded-full font-semibold text-sm ${getMatchColor(opportunity?.matchPercentage)}`}>
            {opportunity?.matchPercentage}% Match
          </div>
        </div>
        {opportunity?.isUrgent && (
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-red-600 text-white rounded-full text-xs font-semibold flex items-center gap-1">
            <Icon name="Clock" size={14} />
            Urgent
          </div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-border">
            <Image
              src={opportunity?.organizationLogo || "https://img.rocket.new/generatedImages/rocket_gen_img_11d2a1522-1764749860533.png"}
              alt={opportunity?.organizationLogoAlt || "Org Logo"}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
              {opportunity?.title}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {opportunity?.organizationName}
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground line-clamp-2">
          {opportunity?.description}
        </p>

        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium uppercase tracking-wide">
            <Icon name={getCauseIcon(opportunity?.cause)} size={12} />
            {opportunity?.cause}
          </span>
          {opportunity?.isRemote && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-medium uppercase tracking-wide">
              <Icon name="Wifi" size={12} />
              Remote
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-y-2 gap-x-4 pt-3 border-t border-border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="MapPin" size={14} className="flex-shrink-0" />
            <span className="truncate">{opportunity?.location}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Clock" size={14} className="flex-shrink-0" />
            <span className="truncate">{opportunity?.timeCommitment}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Calendar" size={14} className="flex-shrink-0" />
            <span className="truncate">{opportunity?.schedule}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="TrendingUp" size={14} className="flex-shrink-0" />
            <span className="truncate">{opportunity?.impactLevel} Impact</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
            <span>Match Score</span>
            <span className="text-primary">{opportunity?.skillsMatch}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
              style={{ width: `${opportunity?.skillsMatch}%` }}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => onViewDetails(opportunity?.id)}
          >
            Details
          </Button>
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="Send"
            iconPosition="right"
            onClick={() => onApply(opportunity?.id)}
          >
            Apply Now
          </Button>
        </div>

        {opportunity?.spotsRemaining && (
          <p className="text-xs text-center text-amber-600 font-medium">
            Only {opportunity?.spotsRemaining} spots remaining
          </p>
        )}
      </div>
    </div>
  );
};

export default OpportunityCard;